import { useState, useEffect, useRef } from "react";
import api from "../api/api";
import { useNavigate } from "react-router-dom";

/* ── Logo (same as HomePage) ── */
const Logo = ({ size = 28, color = "#2563eb" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100">
    <defs><filter id="lf2"><feColorMatrix type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="r"/><feOffset in="r" dx="-1.5" result="rO"/><feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="g"/><feOffset in="g" dx="1.5" result="gO"/><feMerge><feMergeNode in="rO"/><feMergeNode in="gO"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
    <g filter="url(#lf2)" stroke={color} fill="none" strokeLinecap="round">
      <path d="M50 18 A32 32 0 0 1 82 50" strokeWidth="5"/><path d="M50 82 A32 32 0 0 1 18 50" strokeWidth="5"/>
      <line x1="14" y1="50" x2="86" y2="50" strokeWidth="4"/><line x1="50" y1="14" x2="50" y2="86" strokeWidth="4"/>
      <circle cx="14" cy="50" r="4" fill={color} stroke="none"/><circle cx="86" cy="50" r="4" fill={color} stroke="none"/>
      <circle cx="50" cy="14" r="4" fill={color} stroke="none"/><circle cx="50" cy="86" r="4" fill={color} stroke="none"/>
      <polygon points="50,40 43,56 57,56" fill={color} stroke="none"/>
      <circle cx="74" cy="26" r="4" fill={color} stroke="none"/><circle cx="26" cy="74" r="4" fill={color} stroke="none"/>
    </g>
  </svg>
);

/* Floating image cards on hero side */
const HERO_IMGS = [
  "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=320&q=80",
  "https://images.unsplash.com/photo-1526947425960-945c6e72858f?w=320&q=80",
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=320&q=80",
  "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=320&q=80",
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
html,body{height:100%;font-family:'Poppins',sans-serif;-webkit-font-smoothing:antialiased;}
body{background:#fff;overflow-x:hidden;}
::-webkit-scrollbar{width:3px;}
::-webkit-scrollbar-thumb{background:#2563eb;border-radius:99px;}

/* ── NAV (same as HomePage) ── */
.ln{
  position:fixed;inset:0 0 auto;z-index:900;
  height:68px;padding:0 clamp(16px,5vw,72px);
  display:flex;align-items:center;justify-content:space-between;
  background:rgba(255,255,255,.92);
  backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);
  border-bottom:1px solid rgba(37,99,235,.09);
  box-shadow:0 4px 24px rgba(37,99,235,.06);
}
.ln-brand{display:flex;align-items:center;gap:10px;cursor:pointer;text-decoration:none;}
.ln-name{font-size:19px;font-weight:800;color:#0f172a;letter-spacing:-.5px;}
.ln-name b{color:#2563eb;}
.ln-r{display:flex;align-items:center;gap:10px;}
.ln-home{font-size:13px;font-weight:500;color:#64748b;cursor:pointer;background:none;border:none;font-family:'Poppins',sans-serif;transition:color .2s;padding:0;}
.ln-home:hover{color:#2563eb;}
.ln-badge{
  display:flex;align-items:center;gap:6px;
  padding:5px 14px;border-radius:99px;
  background:rgba(37,99,235,.07);border:1px solid rgba(37,99,235,.14);
  font-size:11.5px;font-weight:600;color:#2563eb;
}
.ln-dot{width:6px;height:6px;border-radius:50%;background:#10b981;animation:ldot 2s ease-in-out infinite;}
@keyframes ldot{0%,100%{opacity:1}50%{opacity:.3}}

/* ── PAGE WRAPPER ── */
.lp{
  min-height:100vh;
  display:grid;grid-template-columns:1fr 1fr;
  padding-top:68px;
}

/* ── LEFT — VISUAL PANEL ── */
.lv{
  background:linear-gradient(160deg,#0f172a 0%,#1e3a8a 55%,#1d4ed8 100%);
  position:relative;overflow:hidden;
  display:flex;flex-direction:column;justify-content:center;align-items:flex-start;
  padding:60px clamp(32px,4vw,64px);
  min-height:calc(100vh - 68px);
}

/* animated grid */
.lv-grid{
  position:absolute;inset:0;pointer-events:none;
  background-image:radial-gradient(circle,rgba(255,255,255,.05) 1px,transparent 1px);
  background-size:28px 28px;
}
/* glowing orbs */
.lv-o1{position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(99,102,241,.22),transparent 70%);top:-200px;right:-150px;pointer-events:none;animation:orba 10s ease-in-out infinite alternate;}
.lv-o2{position:absolute;width:350px;height:350px;border-radius:50%;background:radial-gradient(circle,rgba(14,165,233,.18),transparent 70%);bottom:-120px;left:-80px;pointer-events:none;animation:orbb 14s ease-in-out infinite alternate;}
.lv-o3{position:absolute;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle,rgba(37,99,235,.25),transparent 70%);top:42%;left:38%;pointer-events:none;animation:orbc 8s ease-in-out infinite alternate;}
@keyframes orba{from{transform:translate(0,0)}to{transform:translate(-40px,30px)}}
@keyframes orbb{from{transform:translate(0,0)}to{transform:translate(30px,-28px)}}
@keyframes orbc{from{transform:translate(0,0)}to{transform:translate(-20px,20px)}}

/* floating particles */
.lv-p{position:absolute;border-radius:50%;background:rgba(255,255,255,.5);pointer-events:none;animation:fp var(--d) ease-in-out var(--dl) infinite alternate;}
@keyframes fp{from{transform:translate(0,0)}to{transform:translate(var(--tx),var(--ty))}}

/* content */
.lv-inner{position:relative;z-index:2;}
.lv-tag{
  display:inline-flex;align-items:center;gap:8px;
  background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.18);
  border-radius:99px;padding:6px 16px 6px 8px;
  font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;
  color:rgba(255,255,255,.85);margin-bottom:28px;
  backdrop-filter:blur(6px);
}
.lv-tag-ic{width:22px;height:22px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#0ea5e9);display:flex;align-items:center;justify-content:center;font-size:10px;color:#fff;}
.lv-tag-dot{width:6px;height:6px;border-radius:50%;background:#10b981;animation:ldot 2s ease-in-out infinite;}
.lv-h1{
  font-size:clamp(32px,3.5vw,52px);
  font-weight:900;color:#fff;line-height:1.1;
  letter-spacing:-1.5px;margin-bottom:18px;
}
.lv-h1 em{font-style:italic;background:linear-gradient(135deg,#93c5fd,#c4b5fd);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
.lv-sub{font-size:15px;color:rgba(255,255,255,.6);line-height:1.8;max-width:380px;margin-bottom:44px;font-weight:400;}

/* stats row */
.lv-stats{display:flex;gap:28px;margin-bottom:44px;}
.lv-stat-n{font-size:30px;font-weight:900;color:#fff;line-height:1;}
.lv-stat-l{font-size:10px;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:1.5px;margin-top:4px;}
.lv-stat-sep{width:1px;background:rgba(255,255,255,.1);align-self:stretch;}

/* floating image cards */
.lv-imgs{
  position:absolute;right:0;top:50%;
  transform:translateY(-50%);
  display:flex;flex-direction:column;gap:14px;
  padding-right:20px;z-index:2;
}
.lv-img{
  width:150px;height:108px;border-radius:14px;overflow:hidden;
  border:1px solid rgba(255,255,255,.12);
  box-shadow:0 16px 36px rgba(0,0,0,.3);
  transition:transform .4s cubic-bezier(.34,1.56,.64,1);
}
.lv-img:nth-child(1){transform:translateX(0) rotate(-2deg);}
.lv-img:nth-child(2){transform:translateX(-20px) rotate(1.5deg);}
.lv-img:nth-child(3){transform:translateX(10px) rotate(-1deg);}
.lv-img:nth-child(4){transform:translateX(-15px) rotate(2deg);}
.lv-img:hover{transform:translateX(-8px) scale(1.05) rotate(0deg) !important;}
.lv-img img{width:100%;height:100%;object-fit:cover;display:block;}

/* brand bottom */
.lv-brand{
  position:absolute;bottom:28px;left:clamp(32px,4vw,64px);
  display:flex;align-items:center;gap:8px;z-index:2;opacity:.5;
}
.lv-brand-name{font-size:13px;font-weight:700;color:#fff;letter-spacing:.5px;}

/* ── RIGHT — FORM PANEL ── */
.lr{
  background:#fff;
  display:flex;flex-direction:column;
  justify-content:center;align-items:center;
  padding:60px clamp(16px,5vw,56px);
  position:relative;overflow:hidden;
  min-height:calc(100vh - 68px);
}
/* subtle bg */
.lr::before{content:'';position:absolute;inset:0;background-image:radial-gradient(circle,rgba(37,99,235,.04) 1px,transparent 1px);background-size:24px 24px;pointer-events:none;}
.lr-orb{position:absolute;width:420px;height:420px;border-radius:50%;background:radial-gradient(circle,rgba(37,99,235,.05),transparent 70%);top:-120px;right:-100px;pointer-events:none;animation:orba 12s ease-in-out infinite alternate;}

/* form box */
.fb{width:100%;max-width:400px;position:relative;z-index:1;}

/* toggle tabs */
.fb-tabs{
  display:flex;background:#f1f5f9;border-radius:14px;padding:4px;gap:3px;
  margin-bottom:32px;
}
.fb-tab{
  flex:1;padding:10px 8px;border:none;border-radius:10px;
  font-family:'Poppins',sans-serif;font-size:13.5px;font-weight:600;
  cursor:pointer;transition:all .25s;color:#64748b;background:transparent;
  display:flex;align-items:center;justify-content:center;gap:7px;
}
.fb-tab.on{background:#fff;color:#2563eb;box-shadow:0 2px 10px rgba(37,99,235,.12);border:1px solid rgba(37,99,235,.12);}
.fb-tab-ic{width:22px;height:22px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:12px;background:rgba(37,99,235,.08);transition:background .25s;}
.fb-tab.on .fb-tab-ic{background:linear-gradient(135deg,#2563eb,#0ea5e9);color:#fff;}

/* heading */
.fb-h{font-size:clamp(22px,3vw,30px);font-weight:800;color:#0f172a;letter-spacing:-.5px;margin-bottom:6px;}
.fb-sub{font-size:13px;color:#94a3b8;margin-bottom:28px;font-weight:400;}

/* labels & inputs */
.fl{display:block;font-size:10.5px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#475569;margin-bottom:7px;}
.fw{position:relative;margin-bottom:16px;}
.fi{
  width:100%;padding:13px 16px 13px 44px;
  border:1.5px solid #e2e8f0;border-radius:12px;
  font-family:'Poppins',sans-serif;font-size:13.5px;
  color:#0f172a;background:#f8fafc;outline:none;transition:all .25s;
}
.fi:focus{border-color:#2563eb;box-shadow:0 0 0 4px rgba(37,99,235,.1);background:#fff;}
.fi::placeholder{color:#cbd5e1;}
/* input icon */
.fi-ic{
  position:absolute;left:14px;top:50%;transform:translateY(-50%);
  font-size:16px;pointer-events:none;opacity:.5;
  transition:opacity .2s;
}
.fw:focus-within .fi-ic{opacity:1;}
/* animated line */
.fi-line{position:absolute;bottom:0;left:0;height:2px;width:0;background:linear-gradient(90deg,#2563eb,#0ea5e9);border-radius:0 0 12px 12px;transition:width .35s cubic-bezier(.4,0,.2,1);}
.fw:focus-within .fi-line{width:100%;}

/* password strength bar */
.pw-str{margin-top:-10px;margin-bottom:14px;}
.pw-str-bar{height:3px;border-radius:99px;background:#e2e8f0;overflow:hidden;margin-bottom:5px;}
.pw-str-fill{height:100%;border-radius:99px;transition:width .3s,background .3s;}
.pw-str-txt{font-size:10px;font-weight:600;color:#94a3b8;}

/* error */
.ferr{background:#fef2f2;border:1px solid #fecaca;color:#dc2626;padding:10px 13px;border-radius:10px;font-size:12px;margin-bottom:14px;font-weight:500;display:flex;align-items:center;gap:7px;}

/* submit */
.fbtn{
  width:100%;padding:14px;
  background:linear-gradient(135deg,#2563eb,#0ea5e9);
  border:none;border-radius:12px;color:#fff;
  font-family:'Poppins',sans-serif;font-size:14.5px;font-weight:800;
  cursor:pointer;transition:all .22s;margin-bottom:16px;letter-spacing:.2px;
  box-shadow:0 6px 22px rgba(37,99,235,.35);position:relative;overflow:hidden;
  display:flex;align-items:center;justify-content:center;gap:8px;
}
.fbtn::after{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(255,255,255,.14),transparent);opacity:0;transition:opacity .2s;}
.fbtn:hover::after{opacity:1;}
.fbtn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 10px 30px rgba(37,99,235,.42);}
.fbtn:disabled{opacity:.45;cursor:not-allowed;}

/* divider */
.fdiv{display:flex;align-items:center;gap:12px;margin-bottom:16px;}
.fdiv-l{flex:1;height:1px;background:#f1f5f9;}
.fdiv-t{font-size:10px;color:#cbd5e1;letter-spacing:1px;text-transform:uppercase;font-weight:600;white-space:nowrap;}

/* toggle link */
.ftog{text-align:center;font-size:13px;color:#94a3b8;}
.ftog-l{color:#2563eb;font-weight:700;cursor:pointer;background:none;border:none;font-family:inherit;font-size:inherit;padding:0;transition:color .2s;}
.ftog-l:hover{color:#1d4ed8;text-decoration:underline;}

/* credits banner */
.fcred{
  margin-top:20px;
  background:linear-gradient(135deg,#eff6ff,#e0f2fe);
  border:1px solid rgba(37,99,235,.15);border-radius:14px;
  padding:14px 16px;display:flex;align-items:center;gap:13px;
  font-size:12.5px;color:#334155;
}
.fcred-ic{
  width:40px;height:40px;border-radius:11px;flex-shrink:0;
  background:linear-gradient(135deg,#2563eb,#0ea5e9);
  display:flex;align-items:center;justify-content:center;
  font-size:18px;box-shadow:0 4px 12px rgba(37,99,235,.28);
}

/* ── FOOTER (same as HomePage) ── */
.lfoot{
  padding:36px clamp(16px,5vw,72px);
  background:#fff;border-top:1px solid rgba(37,99,235,.08);
  display:flex;align-items:center;justify-content:space-between;
  flex-wrap:wrap;gap:16px;
}
.lfoot-brand{display:flex;align-items:center;gap:9px;}
.lfoot-name{font-size:16px;font-weight:800;color:#0f172a;letter-spacing:-.4px;}
.lfoot-name b{color:#2563eb;}
.lfoot-copy{font-size:12px;color:#cbd5e1;}
.lfoot-parent{font-size:11px;color:#94a3b8;}
.lfoot-parent b{color:#64748b;}
.lfoot-links{display:flex;gap:20px;}
.lfoot-link{font-size:12.5px;color:#64748b;cursor:pointer;background:none;border:none;font-family:'Poppins',sans-serif;padding:0;transition:color .2s;}
.lfoot-link:hover{color:#2563eb;}

/* ── SUCCESS / LOADING SCREENS ── */
.fs{position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;text-align:center;padding:40px;font-family:'Poppins',sans-serif;}
.fs-light{background:linear-gradient(135deg,#f0f6ff,#fff,#e8f1ff);}
.fs-dark{background:linear-gradient(160deg,#0f172a 0%,#1e3a8a 55%,#1d4ed8 100%);}
.fs-logo{font-size:24px;font-weight:800;}
.fs-light .fs-logo{color:#2563eb;}
.fs-dark .fs-logo{color:#fff;}
.fs-logo b{font-style:italic;}
.spin{width:48px;height:48px;border-radius:50%;border:3px solid rgba(37,99,235,.15);border-top-color:#2563eb;animation:spin .85s linear infinite;}
.fs-dark .spin{border-color:rgba(255,255,255,.15);border-top-color:rgba(255,255,255,.85);}
@keyframes spin{to{transform:rotate(360deg)}}
.fs-txt{font-size:13px;font-weight:500;}
.fs-light .fs-txt{color:#64748b;}
.fs-dark .fs-txt{color:rgba(255,255,255,.6);}
.fs-check{width:84px;height:84px;border-radius:50%;display:flex;align-items:center;justify-content:center;animation:pop .5s cubic-bezier(.34,1.56,.64,1) .1s both;}
.fs-dark .fs-check{background:rgba(255,255,255,.12);border:2px solid rgba(255,255,255,.22);}
.fs-light .fs-check{background:linear-gradient(135deg,#2563eb,#0ea5e9);box-shadow:0 12px 36px rgba(37,99,235,.35);}
@keyframes pop{from{opacity:0;transform:scale(.2)}to{opacity:1;transform:scale(1)}}
.fs-h{font-size:clamp(26px,4vw,40px);font-weight:800;letter-spacing:-.5px;}
.fs-dark .fs-h{color:#fff;}
.fs-light .fs-h{color:#0f172a;}
.fs-sub{font-size:14px;max-width:320px;line-height:1.75;}
.fs-dark .fs-sub{color:rgba(255,255,255,.62);}
.fs-light .fs-sub{color:#64748b;}
.fs-pill{background:rgba(255,255,255,.1);border:1.5px solid rgba(255,255,255,.2);border-radius:99px;padding:12px 26px;display:flex;align-items:center;gap:9px;color:#fff;font-size:14px;font-weight:600;backdrop-filter:blur(8px);}
.prog-bar{width:180px;height:3px;border-radius:99px;overflow:hidden;}
.fs-dark .prog-bar{background:rgba(255,255,255,.12);}
.fs-light .prog-bar{background:#dbeafe;}
.prog-fill{height:100%;border-radius:99px;}
.fs-dark .prog-fill{background:rgba(255,255,255,.75);animation:fill 2.4s linear .5s both;}
.fs-light .prog-fill{background:#2563eb;animation:fill 1.8s linear .4s both;}
@keyframes fill{from{width:0}to{width:100%}}
.fs-hint{font-size:12px;}
.fs-dark .fs-hint{color:rgba(255,255,255,.35);}
.fs-light .fs-hint{color:#94a3b8;}
.ai{opacity:0;transform:translateY(14px);animation:airi .45s ease forwards;}
.ai:nth-child(1){animation-delay:.05s}.ai:nth-child(2){animation-delay:.12s}.ai:nth-child(3){animation-delay:.18s}.ai:nth-child(4){animation-delay:.24s}.ai:nth-child(5){animation-delay:.3s}.ai:nth-child(6){animation-delay:.36s}.ai:nth-child(7){animation-delay:.42s}
@keyframes airi{to{opacity:1;transform:translateY(0)}}

/* ── RESPONSIVE ── */
@media(max-width:900px){
  .lp{grid-template-columns:1fr;}
  .lv{display:none;}
  .lr{min-height:calc(100vh - 68px);padding:40px 24px;}
  .lfoot{flex-direction:column;align-items:flex-start;gap:12px;}
}
@media(max-width:480px){
  .ln{height:58px;padding:0 14px;}
  .ln-name{font-size:17px;}
  .ln-badge{font-size:10.5px;padding:4px 11px;}
  .lr{padding:28px 16px;}
  .fb{max-width:100%;}
  .fb-h{font-size:22px;}
  .fb-sub{font-size:12px;margin-bottom:20px;}
  .fb-tabs{margin-bottom:24px;}
  .fb-tab{font-size:12.5px;padding:9px 6px;}
  .fi{font-size:13px;padding:12px 14px 12px 42px;}
  .fbtn{font-size:13.5px;padding:13px;}
  .lfoot{padding:28px 14px;}
  .lfoot-links{gap:14px;}
}
`;

/* password strength helper */
function pwStrength(p) {
  if (!p) return { w: 0, c: "#e2e8f0", t: "" };
  let s = 0;
  if (p.length >= 6)  s++;
  if (p.length >= 10) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  const map = [
    { w:0,  c:"#e2e8f0", t:"" },
    { w:20, c:"#ef4444", t:"Weak" },
    { w:40, c:"#f59e0b", t:"Fair" },
    { w:65, c:"#3b82f6", t:"Good" },
    { w:85, c:"#10b981", t:"Strong" },
    { w:100,c:"#10b981", t:"Very strong" },
  ];
  return map[s] || map[0];
}

export default function Login() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [isReg,    setIsReg]    = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState(null);
  const nav = useNavigate();

  const heroRef = useRef(null);

  /* subtle mouse parallax on visual panel */
  useEffect(() => {
    const mv = e => {
      if (!heroRef.current) return;
      const { innerWidth:w, innerHeight:h } = window;
      const x = (e.clientX/w - .5) * 18;
      const y = (e.clientY/h - .5) * 12;
      heroRef.current.querySelectorAll(".lv-img").forEach((el,i) => {
        const d = (i%2===0?1:-1) * (i+1) * .4;
        el.style.transform = `translateX(${x*d - (i%2===0?0:20)}px) translateY(${y*d}px) rotate(${i%2===0?-2+x*.05:1.5-x*.04}deg)`;
      });
      heroRef.current.querySelectorAll(".lv-o1,.lv-o2,.lv-o3").forEach((el,i) => {
        el.style.transform = `translate(${x*(i+1)*.6}px,${y*(i+1)*.4}px)`;
      });
    };
    window.addEventListener("mousemove", mv);
    return () => window.removeEventListener("mousemove", mv);
  }, []);

  const submit = async () => {
    if (!email || !password) { setError("Please fill in all fields."); return; }
    setError(""); setLoading(true);
    try {
      if (isReg) {
        const res = await api.post("/register", { email, password });
        localStorage.setItem("user", JSON.stringify(res.data));
        setLoading(false); setSuccess("register");
        setTimeout(() => nav("/dashboard"), 2600);
      } else {
        const res = await api.post("/login", { email, password });
        localStorage.setItem("user", JSON.stringify(res.data));
        setLoading(false); setSuccess("login");
        setTimeout(() => nav("/dashboard"), 2000);
      }
    } catch(e) {
      setError(e.response?.data?.detail || "Something went wrong. Try again.");
      setLoading(false);
    }
  };

  const pw = pwStrength(password);

  /* ── LOADING ── */
  if (loading) return (
    <div className="fs fs-light">
      <style>{CSS}</style>
      <div className="fs-logo ai">Pic<b>9</b>ine</div>
      <div className="spin ai"/>
      <div className="fs-txt ai">{isReg?"Creating your account…":"Signing you in…"}</div>
    </div>
  );

  /* ── REGISTER SUCCESS ── */
  if (success === "register") return (
    <div className="fs fs-dark">
      <style>{CSS}</style>
      <style>{`@keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(255,255,255,.25)}50%{box-shadow:0 0 0 18px rgba(255,255,255,0)}}`}</style>
      <div className="fs-check ai" style={{animation:"pop .5s cubic-bezier(.34,1.56,.64,1) .1s both,pulse 2s ease 1s infinite"}}>
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h1 className="fs-h ai">Account Created!</h1>
      <p className="fs-sub ai">Welcome to Pic9ine. Your workspace is ready with free credits to start creating.</p>
      <div className="fs-pill ai"><Logo size={20} color="white"/>4 Free Credits Added</div>
      <div className="prog-bar ai"><div className="prog-fill"/></div>
      <div className="fs-hint ai">Taking you to dashboard…</div>
    </div>
  );

  /* ── LOGIN SUCCESS ── */
  if (success === "login") {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return (
      <div className="fs fs-light">
        <style>{CSS}</style>
        <style>{`@keyframes pulse2{0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,.3)}50%{box-shadow:0 0 0 18px rgba(37,99,235,0)}}`}</style>
        <div className="fs-logo ai">Pic<b>9</b>ine</div>
        <div className="fs-check ai" style={{animation:"pop .5s cubic-bezier(.34,1.56,.64,1) .1s both,pulse2 2s ease 1s infinite"}}>
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h1 className="fs-h ai">Welcome Back!</h1>
        <p className="fs-sub ai" style={{color:"#2563eb",fontWeight:600}}>{u.email}</p>
        <div className="prog-bar ai"><div className="prog-fill"/></div>
        <div className="fs-hint ai">Loading your workspace…</div>
      </div>
    );
  }

  /* ── MAIN FORM ── */
  return (
    <>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav className="ln">
        <div className="ln-brand" onClick={() => nav("/")}>
          <Logo size={26} color="#2563eb"/>
          <span className="ln-name">Pic<b>9</b>ine</span>
        </div>
        <div className="ln-r">
          <button className="ln-home" onClick={() => nav("/")}>← Home</button>
          <div className="ln-badge">
            <div className="ln-dot"/>
            AI Image Studio
          </div>
        </div>
      </nav>

      {/* ── PAGE ── */}
      <div className="lp">

        {/* LEFT visual panel */}
        <div className="lv" ref={heroRef}>
          <div className="lv-grid"/>
          <div className="lv-o1"/><div className="lv-o2"/><div className="lv-o3"/>

          {/* particles */}
          {Array.from({length:12},(_,i) => (
            <div key={i} className="lv-p" style={{
              left:`${10+Math.random()*80}%`, top:`${10+Math.random()*80}%`,
              width:`${2+Math.random()*4}px`, height:`${2+Math.random()*4}px`,
              opacity: .1+Math.random()*.3,
              "--d":`${6+Math.random()*8}s`, "--dl":`${Math.random()*4}s`,
              "--tx":`${(Math.random()-.5)*40}px`, "--ty":`${(Math.random()-.5)*36}px`,
            }}/>
          ))}

          <div className="lv-inner">
            <div className="lv-tag">
              <div className="lv-tag-ic">✦</div>
              <div className="lv-tag-dot"/>
              Nano Banana AI
            </div>
            <h1 className="lv-h1">
              Create <em>stunning</em><br/>
              images instantly.
            </h1>
            <p className="lv-sub">
              Generate photorealistic images from text. Fashion, products, food — no skills needed.
            </p>
            <div className="lv-stats">
              {[["4","Free Credits"],["∞","Possibilities"],["HD","1024×1024"]].map(([n,l],i,a) => (
                <div key={i} style={{display:"contents"}}>
                  {i>0 && <div className="lv-stat-sep"/>}
                  <div>
                    <div className="lv-stat-n">{n}</div>
                    <div className="lv-stat-l">{l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* floating image cards */}
          <div className="lv-imgs">
            {HERO_IMGS.map((url,i) => (
              <div key={i} className="lv-img">
                <img src={url} alt="ai" loading="lazy"/>
              </div>
            ))}
          </div>

          {/* brand bottom */}
          <div className="lv-brand">
            <Logo size={16} color="rgba(255,255,255,.6)"/>
            <span className="lv-brand-name">Pic9ine</span>
          </div>
        </div>

        {/* RIGHT form panel */}
        <div className="lr">
          <div className="lr-orb"/>
          <div className="fb">

            {/* mode tabs */}
            <div className="fb-tabs">
              <button className={`fb-tab${!isReg?" on":""}`} onClick={() => { setIsReg(false); setError(""); }}>
                <div className="fb-tab-ic">→</div>
                Sign In
              </button>
              <button className={`fb-tab${isReg?" on":""}`} onClick={() => { setIsReg(true); setError(""); }}>
                <div className="fb-tab-ic">✦</div>
                Register
              </button>
            </div>

            <h2 className="fb-h">{isReg ? "Create your account" : "Welcome back"}</h2>
            <p className="fb-sub">{isReg ? "Join thousands of creators today" : "Sign in to continue creating"}</p>

            {/* email */}
            <label className="fl">Email Address</label>
            <div className="fw">
              <span className="fi-ic">✉</span>
              <input className="fi" type="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key==="Enter" && submit()}/>
              <div className="fi-line"/>
            </div>

            {/* password */}
            <label className="fl">Password</label>
            <div className="fw">
              <span className="fi-ic">🔒</span>
              <input className="fi" type="password" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key==="Enter" && submit()}/>
              <div className="fi-line"/>
            </div>

            {/* password strength (register only) */}
            {isReg && password && (
              <div className="pw-str">
                <div className="pw-str-bar">
                  <div className="pw-str-fill" style={{width:`${pw.w}%`,background:pw.c}}/>
                </div>
                <div className="pw-str-txt" style={{color:pw.c}}>{pw.t}</div>
              </div>
            )}

            {error && (
              <div className="ferr">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <button className="fbtn" onClick={submit} disabled={loading}>
              {isReg ? (
                <>✦ Create Account →</>
              ) : (
                <>→ Sign In</>
              )}
            </button>

            <div className="fdiv">
              <div className="fdiv-l"/><div className="fdiv-t">or</div><div className="fdiv-l"/>
            </div>

            <div className="ftog">
              {isReg ? "Already have an account? " : "Don't have an account? "}
              <button className="ftog-l" onClick={() => { setIsReg(!isReg); setError(""); }}>
                {isReg ? "Sign In" : "Register Free"}
              </button>
            </div>

            {isReg && (
              <div className="fcred">
                <div className="fcred-ic">🎁</div>
                <span>Get <strong style={{color:"#2563eb"}}>4 free credits</strong> instantly — no card required.</span>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── FOOTER (matching HomePage) ── */}
      <footer className="lfoot">
        <div className="lfoot-brand">
          <Logo size={20} color="#2563eb"/>
          <span className="lfoot-name">Pic<b>9</b>ine</span>
          <span style={{fontSize:12,color:"#cbd5e1",marginLeft:4}}>AI Image Studio</span>
        </div>
        <div className="lfoot-parent">
          A <b>seo9ine</b> product · <span style={{fontSize:10.5,color:"#cbd5e1"}}>seo9ine is the parent company</span>
        </div>
        <div className="lfoot-copy">© 2026 Pic9ine · All rights reserved</div>
        <div className="lfoot-links">
          <button className="lfoot-link" onClick={() => nav("/")}>Home</button>
          <button className="lfoot-link" onClick={() => nav("/")}>Features</button>
          <button className="lfoot-link" onClick={() => nav("/")}>Pricing</button>
        </div>
      </footer>
    </>
  );
}